import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { PopupMessageComponent } from '../../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { Icons } from '../../../../shared/helpers/icons';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { ThreatAssessmentSummaryService } from '../../../../shared/services/threatAssessment.service';
import { IThreatAssessmentSummary } from '../../../../shared/models/threatAssessmentSummary';
import { ThreatAssessmentInfoComponent } from './threat-assessment-info/threat-assessment-info.component';
import { ThreatAssessmentSummarySignatureComponent } from './threat-assessment-summary-signature/threat-assessment-summary-signature.component';
import { ThreatAssessmentSummarySubmissionComponent } from './threat-assessment-summary-submission/threat-assessment-summary-submission.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'threat-assessment-summary',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    SpinnerComponent,
    PopupMessageComponent,
    ThreatAssessmentInfoComponent,
    ThreatAssessmentSummarySignatureComponent,
    ThreatAssessmentSummarySubmissionComponent
  ],
  templateUrl: './threat-assessment-summary.component.html',
  styleUrl: './threat-assessment-summary.component.css'
})
export class ThreatAssessmentSummaryComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;

  currentCaseId: string | null = null;
  currentRoarId: string | null = null;
  currentThreatAssessmentSummary: any | null = null;
  stepsConfig = [
    {
      id: 1,
      completionProperty: 'threat_assessment_info_section_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'official_signatures_section_completion',
      nextStepDependency: 'threat_assessment_info_section_completion'
    }
  ];
  finalStepConfig = {
      id: 3,
      completionProperty: 'threat_assessment_summary_completion',
      nextStepDependency: 'official_signatures_section_completion'
  };
  steps = [1, 2];
  currentStep: number = 1;
  tooltips = ['Threat Assessment Info', 'Official Signature', 'Submission'];
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataservice: UserDataService,
    private threatAssessmentSummaryService: ThreatAssessmentSummaryService,
    private toastr: ToastrService
  ) {}
  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataservice.getBuildingId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
    if (this.currentCaseId) {
      this.loadThreatAssessmentSummary();
    }
  }

  loadThreatAssessmentSummary(): void {
    if (this.currentCaseId) {
      this.loading = true;
      this.threatAssessmentSummaryService.getThreatAssessmentSummaryByCaseId(this.currentCaseId).subscribe({
        next: (response) => {
          if (Object.keys(response).length === 0) {
            this.currentThreatAssessmentSummary = null;
            this.loading = false;
            return
          } else {
            this.currentThreatAssessmentSummary = response;
            this.loading = false;
            return
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error:', error);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      });
    }
  }

  handleUpdateThreatAssessmentSummary(data: IThreatAssessmentSummary, section: number): void {
    if (this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);
      this.threatAssessmentSummaryService.updateThreatAssessmentSummary(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.loadThreatAssessmentSummary();
          this.toastr.success('Saved successful!', 'Success');
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 3 ) {
            this.router.navigate(['/interventions/']);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating threat assessment summary:', error);
          this.toastr.error(error.message, 'Error');
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/interventions/']);
  }
  
  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }
  
    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }
  
    if (stepConfig.nextStepDependency && this.currentThreatAssessmentSummary) {
      return this.currentThreatAssessmentSummary?.[stepConfig.nextStepDependency] === 1;
    }
  
    return true;
  }

  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

}
